import { useEffect } from "react";
import { cn } from "@/lib/utils";
import type { SlashMenuItem } from "@/services/multi-agent/slashCommands";

/**
 * The composer's `/` list: commands, or starter prompts after `/prompts`.
 *
 * WHY NOT A `Popover`. The composer's other menus (`ComposerModeMenu`, `ContextDial`) open from a
 * button and move focus into themselves. This one opens from TYPING, so focus has to stay in the
 * textarea — the reader is mid-word. It is therefore a plain list positioned above the composer and
 * driven by the textarea's own keys; the textarea points at the highlighted row with
 * `aria-activedescendant`. Leaving focus where it is also keeps the textarea's blur handler (typed
 * URLs become attachments) from firing every time the list opens.
 *
 * WHAT IT DOES NOT DECIDE. Which rows exist, which are greyed and why, and what a confirmation says
 * are all `slashCommands.ts`. This renders rows it is given. Pure props, so the markup can be
 * render-tested.
 *
 * ACCESSIBLE CONTRACT. `role="listbox"` of `role="option"` rows; the highlighted row carries
 * `aria-selected`; a command that cannot run now stays listed with `aria-disabled` and its reason in
 * text (never colour alone, §16), because a missing row would leave the reader guessing whether the
 * command exists. A pending confirmation is `role="alert"` so it is announced when it appears.
 */
export const SLASH_MENU_ID = "weave-slash-menu";

export function slashOptionId(index: number): string {
  return `${SLASH_MENU_ID}-opt-${index}`;
}

export interface SlashCommandMenuProps {
  items: readonly SlashMenuItem[];
  activeIndex: number;
  /** Names the list: "Commands" or "Starter prompts". */
  heading: string;
  /** What a second Enter will do, when one is being asked for; replaces the key hint. */
  confirmText: string | null;
  onPick: (index: number) => void;
}

export function SlashCommandMenu({ items, activeIndex, heading, confirmText, onPick }: SlashCommandMenuProps) {
  /*
   * THE HIGHLIGHT MUST STAY IN VIEW. The list is taller than its box once there are more than a
   * handful of commands, and the arrow keys move the highlight, not the scroll. Without this the
   * highlight walks off the bottom and the keys look dead. `nearest` moves only as far as needed,
   * so a row already on screen never jumps. (No effect in the static render the tests use.)
   */
  const count = items.length;
  useEffect(() => {
    if (count === 0) return;
    document.getElementById(slashOptionId(activeIndex))?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, count]);

  if (items.length === 0) return null;
  return (
    <div
      data-testid="slash-menu"
      // Width capped against the viewport: the composer sits near the edge on a narrow panel (§8.3).
      className="absolute bottom-full left-0 z-20 mb-2 w-[min(26rem,calc(100vw-24px))] rounded-lg border border-border bg-popover text-popover-foreground shadow-md"
    >
      <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{heading}</p>
      {/* Height capped and scrolled inside, never the page (§8.5). */}
      <ul id={SLASH_MENU_ID} role="listbox" aria-label={heading} className="max-h-72 overflow-y-auto p-1">
        {items.map((item, index) => {
          const active = index === activeIndex;
          return (
            <li
              key={item.key}
              id={slashOptionId(index)}
              role="option"
              aria-selected={active}
              aria-disabled={item.disabled || undefined}
              data-slash-key={item.key}
              // Keep focus in the textarea: a mouse press on a row must not blur it.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(index)}
              className={cn(
                "flex min-h-8 cursor-pointer items-baseline gap-3 rounded-md px-2.5 py-1.5 text-[13px] motion-reduce:transition-none",
                // The highlighted row has to be findable at a glance: a tint and an inset bar, not grey on grey.
                active
                  ? "bg-[hsl(var(--primary)/0.12)] shadow-[inset_2px_0_0_hsl(var(--primary))]"
                  : "hover:bg-muted/50",
                item.disabled && "cursor-not-allowed",
              )}
            >
              <span className={cn("shrink-0 font-medium", item.disabled ? "text-muted-foreground" : "text-foreground")}>
                {item.label}
              </span>
              <span className="min-w-0 flex-1 text-[12px] leading-snug text-muted-foreground">
                {item.disabled && item.reason ? item.reason : item.description}
              </span>
            </li>
          );
        })}
      </ul>
      {confirmText ? (
        <p role="alert" className="border-t border-border px-3 py-2 text-[12px] leading-snug text-foreground">
          {confirmText}
        </p>
      ) : (
        <p className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">Enter to run, Esc to close</p>
      )}
    </div>
  );
}
